(() => {
  if (customElements.get('edvi-product-detail')) return;

  class EdviProductDetail extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.observers = [];
      this.animations = new Map();
      this.busy = false;
      this.index = 0;
      this.data = JSON.parse(this.querySelector('[data-evp-data]').textContent);
      this.form = this.querySelector('form');
      this.select = this.querySelector('[data-evp-variant]');
      this.options = [...this.querySelectorAll('[data-evp-option]')];
      this.submit = this.querySelector('[data-evp-submit]');
      this.stickySubmit = this.querySelector('[data-evp-sticky-submit]');
      this.status = this.querySelector('[data-evp-status]');
      this.track = this.querySelector('[data-evp-track]');
      this.slides = [...this.querySelectorAll('[data-evp-media]')];
      this.dots = [...this.querySelectorAll('[data-evp-dot]')];
      this.dotRail = this.querySelector('.evp__dots');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 699px)');
      this.querySelector('[data-evp-native]').hidden = true;
      this.querySelectorAll('[data-evp-enhanced]').forEach(element => { element.hidden = false; });
      // JavaScript manages exclusivity so closing panels retain their height animation.
      this.querySelectorAll('.evp__accordion').forEach(details => details.removeAttribute('name'));
      this.listen(this, 'change', event => {
        if (event.target.matches('[data-evp-option] input')) this.chooseVariant();
      });
      // Capture prevents the older theme-wide cart handler from submitting twice.
      this.listen(this.form, 'submit', event => this.addToCart(event), { capture: true });
      this.listen(this, 'click', event => this.onClick(event));
      this.listen(this.track, 'scroll', () => this.scheduleGallery(), { passive: true });
      this.listen(window, 'resize', () => this.scheduleGallery(), { passive: true });
      this.listen(this.mobile, 'change', () => this.hydrateGallery());
      this.listen(window, 'popstate', () => this.restoreVariant());
      this.listen(this.track, 'keydown', event => {
        if (event.target.closest('video,iframe,model-viewer')) return;
        if (this.mobile.matches && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          const rtl = getComputedStyle(this).direction === 'rtl';
          const direction = (event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1);
          this.goTo(event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : this.index + direction);
        }
      });
      this.querySelectorAll('dialog').forEach(dialog => {
        this.listen(dialog, 'close', () => this.closeDialog(dialog));
        this.listen(dialog, 'click', event => {
          if (event.target !== dialog) return;
          const rect = dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
        });
      });
      const sticky = this.querySelector('[data-evp-sticky]');
      if (sticky) {
        sticky.hidden = false;
        let shown = null;
        const apply = (next) => {
          if (shown === next) return;
          shown = next;
          sticky.classList.toggle('is-visible', shown);
          sticky.inert = !shown;
          sticky.setAttribute('aria-hidden', String(!shown));
        };
        const button = this.querySelector('[data-evp-submit]');
        apply(false);
        if (button && 'IntersectionObserver' in window) {
          const observer = new IntersectionObserver(entries => {
            const entry = entries[entries.length - 1];
            apply(!entry.isIntersecting && entry.boundingClientRect.top < 0);
          });
          observer.observe(button);
          this.controller.signal.addEventListener('abort', () => observer.disconnect());
        }
      }
      this.updateVariant(this.selectedVariant());
      this.updateGallery();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observers?.forEach(observer => observer.disconnect());
      this.animations?.forEach(animation => animation.cancel());
      cancelAnimationFrame(this.galleryFrame);
      this.querySelectorAll('dialog[open]').forEach(dialog => {
        dialog.close();
        document.body.style.overflow = this.previousOverflow || '';
      });
    }

    listen(target, type, listener, options = {}) {
      target.addEventListener(type, listener, { ...options, signal: this.signal });
    }

    selectedVariant() {
      return this.data.variants.find(variant => String(variant.id) === this.select.value);
    }

    chooseVariant() {
      const values = this.options.map(option => option.querySelector('input:checked')?.value);
      const variant = this.data.variants.find(candidate => candidate.options.every((value, index) => value === values[index]));
      this.select.value = variant ? String(variant.id) : '';
      this.updateVariant(variant);
      const url = new URL(location.href);
      if (variant) url.searchParams.set('variant', variant.id);
      else url.searchParams.delete('variant');
      history.replaceState(history.state, '', url);
      if (variant?.mediaId) {
        const index = this.slides.findIndex(slide => slide.dataset.evpMedia === String(variant.mediaId));
        if (index >= 0 && this.mobile.matches) this.goTo(index);
      }
    }

    restoreVariant() {
      const id = new URL(location.href).searchParams.get('variant');
      const variant = this.data.variants.find(candidate => String(candidate.id) === id);
      if (!variant) return;
      this.select.value = String(variant.id);
      this.options.forEach((option, index) => {
        option.querySelectorAll('input').forEach(input => { input.checked = input.value === variant.options[index]; });
      });
      this.updateVariant(variant);
    }

    updateVariant(variant) {
      this.variant = variant;
      this.slides.forEach(slide => {
        slide.toggleAttribute('data-evp-selected-media', !!variant?.mediaId && slide.dataset.evpMedia === String(variant.mediaId));
      });
      this.querySelectorAll('[data-evp-set],.evp__set-badge').forEach(link => {
        const url = new URL(link.href, location.href);
        if (variant?.available) url.searchParams.set('preselect_variant', variant.id);
        else url.searchParams.delete('preselect_variant');
        link.href = url.href;
      });
      this.options.forEach((option, index) => {
        const checked = option.querySelector('input:checked');
        option.querySelector('[data-evp-option-value]').textContent = checked?.value || '';
        option.querySelectorAll('input').forEach(input => {
          // Availability follows preceding options, keeping alternate combinations reachable.
          const available = this.data.variants.some(candidate => candidate.available && candidate.options[index] === input.value && this.options.slice(0, index).every((previous, previousIndex) => candidate.options[previousIndex] === previous.querySelector('input:checked')?.value));
          input.closest('label').classList.toggle('is-unavailable', !available);
        });
      });
      const price = this.querySelector('[data-evp-price]');
      const compare = this.querySelector('[data-evp-compare]');
      if (variant) {
        price.innerHTML = variant.price;
        compare.innerHTML = variant.compare || '';
        this.querySelector('[data-evp-sticky-price]').innerHTML = variant.price;
        this.querySelector('[data-evp-quantity]').value = variant.quantity;
      } else {
        price.textContent = this.data.text.unavailable;
        this.querySelector('[data-evp-sticky-price]').textContent = this.data.text.unavailable;
      }
      compare.hidden = !variant?.compare;
      this.querySelector('[data-evp-stock]').hidden = !variant?.available;
      this.updateButtons();
    }

    updateButtons() {
      const label = this.busy ? this.data.text.adding : !this.variant ? this.data.text.unavailable : this.variant.available ? this.data.text.add : this.data.text.soldOut;
      for (const button of [this.submit, this.stickySubmit]) {
        button.disabled = this.busy || !this.variant?.available;
        button.textContent = label;
        button.setAttribute('aria-busy', String(this.busy));
      }
    }

    async addToCart(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.busy || !this.variant?.available || !this.form.reportValidity()) return;
      this.busy = true;
      this.updateButtons();
      this.status.textContent = '';
      this.status.dataset.error = 'false';
      const trigger = event.submitter || this.submit;
      const addon = this.querySelector('[data-evp-addon]');
      const items = [{ id: this.variant.id, quantity: this.variant.quantity }];
      if (addon?.checked) items.push({ id: Number(addon.dataset.evpAddon), quantity: Number(addon.dataset.quantity) || 1 });
      try {
        if (window.EdviBag?.add) {
          await window.EdviBag.add({ items });
        } else {
          const addItems = async () => {
            const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
              method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items })
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.description || this.data.text.error);
          };
          // The complete pair is one items[] request; mutations never run in parallel.
          if (navigator.locks) await navigator.locks.request('edvi-cart-mutations', addItems);
          else await addItems();
        }
        if (!this.isConnected) return;
        this.status.textContent = this.data.text.added;
        await this.openCartFeedback(trigger, items);
      } catch (error) {
        this.status.textContent = error.message || this.data.text.error;
        this.status.dataset.error = 'true';
      } finally {
        this.busy = false;
        this.updateButtons();
      }
    }

    async openCartFeedback(trigger, items) {
      // A presentation failure must never turn a successful add into a retry prompt.
      try {
        if (window.EdviBag?.open) await window.EdviBag.open(trigger);
        else if (window.edviCart?.open) await window.edviCart.open(trigger);
        else if (window.edviCart?.refresh) await window.edviCart.refresh(true);
        if (document.querySelector('[data-cart-drawer][open]')) return;
      } catch { /* The PDP confirmation remains available if the global cart is not ready. */ }
      const dialog = this.querySelector('[data-evp-cart-dialog]');
      const host = this.querySelector('[data-evp-cart-items]');
      host.replaceChildren();
      this.openDialog(dialog, trigger);
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' }, cache: 'no-store', signal: this.signal
        });
        if (!response.ok) return;
        const cart = await response.json();
        const addedIds = new Set(items.map(item => String(item.id)));
        const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: cart.currency || 'USD' });
        for (const item of cart.items || []) {
          if (!addedIds.has(String(item.variant_id))) continue;
          const row = document.createElement('article');
          row.className = 'evp__cart-item';
          if (item.image) {
            const imageURL = new URL(item.image, location.origin);
            if (['http:', 'https:'].includes(imageURL.protocol)) {
              imageURL.searchParams.set('width', '144');
              const image = new Image(72, 72);
              image.src = imageURL.href;
              image.alt = '';
              row.append(image);
            }
          }
          const copy = document.createElement('div');
          const title = document.createElement('strong');
          title.textContent = item.product_title || item.title;
          const detail = document.createElement('p');
          const quantity = items.find(added => String(added.id) === String(item.variant_id))?.quantity || 1;
          detail.textContent = [item.variant_title !== 'Default Title' ? item.variant_title : '', `${quantity} × ${formatter.format(item.final_price / 100)}`].filter(Boolean).join(' · ');
          copy.append(title, detail);
          row.append(copy);
          host.append(row);
        }
        document.querySelectorAll('[data-cart-count]').forEach(counter => {
          counter.textContent = String(cart.item_count);
          counter.hidden = !cart.item_count;
        });
      } catch { /* The confirmed add and View cart link remain visible. */ }
    }

    onClick(event) {
      const target = event.target.closest('button,a,summary');
      if (!target) return;
      if (target.matches('[data-evp-zoom]')) {
        event.preventDefault();
        const dialog = this.querySelector('[data-evp-zoom-dialog]');
        const canvas = this.querySelector('[data-evp-zoom-canvas]');
        const image = new Image();
        image.src = target.href;
        image.alt = target.dataset.alt;
        image.decoding = 'async';
        canvas.replaceChildren(image);
        canvas.classList.remove('is-magnified');
        this.querySelector('[data-evp-magnify]').setAttribute('aria-pressed', 'false');
        this.openDialog(dialog, target);
      }
      if (target.matches('[data-evp-magnify]')) {
        const canvas = this.querySelector('[data-evp-zoom-canvas]');
        const magnified = canvas.classList.toggle('is-magnified');
        target.setAttribute('aria-pressed', String(magnified));
        requestAnimationFrame(() => canvas.scrollTo({ left: magnified ? (canvas.scrollWidth - canvas.clientWidth) / 2 : 0, top: magnified ? (canvas.scrollHeight - canvas.clientHeight) / 2 : 0, behavior: 'instant' }));
      }
      if (target.matches('[data-evp-open]')) {
        event.preventDefault();
        this.openDialog(this.querySelector(`#${CSS.escape(target.dataset.evpOpen)}`), target);
      }
      if (target.matches('[data-evp-close]')) target.closest('dialog').close();
      if (target.matches('[data-evp-step]')) this.goTo(this.index + Number(target.dataset.evpStep));
      if (target.matches('[data-evp-dot]')) this.goTo(Number(target.dataset.evpDot));
      if (target.matches('.evp__accordion summary')) {
        event.preventDefault();
        const details = target.parentElement;
        const opening = !(details.open && details.dataset.closing !== 'true');
        if (opening) this.querySelectorAll('.evp__accordion[open]').forEach(other => { if (other !== details) this.animateAccordion(other, false); });
        this.animateAccordion(details, opening);
      }
    }

    openDialog(dialog, trigger) {
      if (!dialog) return;
      this.dialogTrigger = trigger;
      this.previousOverflow = document.body.style.overflow;
      dialog.showModal();
      document.body.style.overflow = 'hidden';
      dialog.querySelector('[data-evp-close]').focus();
    }

    closeDialog(dialog) {
      document.body.style.overflow = this.previousOverflow || '';
      if (this.dialogTrigger?.isConnected) this.dialogTrigger.focus({ preventScroll: true });
      if (dialog.matches('[data-evp-zoom-dialog]')) this.querySelector('[data-evp-zoom-canvas]').classList.remove('is-magnified');
    }

    animateAccordion(details, opening) {
      const body = details.querySelector('.evp__accordion-body');
      const start = details.open ? body.getBoundingClientRect().height : 0;
      this.animations.get(details)?.cancel();
      details.dataset.closing = String(!opening);
      if (opening) details.open = true;
      if (this.reduced.matches || !body.animate) { details.open = opening; return; }
      const animation = body.animate([{ height: `${start}px` }, { height: `${opening ? body.scrollHeight : 0}px` }], { duration: 500, easing: 'cubic-bezier(.17,.67,.09,.97)' });
      this.animations.set(details, animation);
      animation.onfinish = () => {
        details.open = opening;
        delete details.dataset.closing;
        this.animations.delete(details);
      };
    }

    goTo(index) {
      if (!this.slides.length || !this.mobile.matches) return;
      this.index = Math.max(0, Math.min(this.slides.length - 1, index));
      this.hydrateGallery();
      const slide = this.slides[this.index];
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      this.track.scrollTo({ left: this.index * this.track.clientWidth * (rtl ? -1 : 1), behavior: this.reduced.matches ? 'instant' : 'smooth' });
      slide.querySelector('video')?.pause();
    }

    scheduleGallery() {
      if (this.galleryFrame) return;
      this.galleryFrame = requestAnimationFrame(() => { this.galleryFrame = null; this.updateGallery(); });
    }

    hydrateGallery() {
      // Native lazy-loading reaches several screens horizontally. Keep later mobile
      // slides inert until navigation approaches them; desktop retains its full grid.
      this.slides.forEach((slide, index) => {
        if (this.mobile.matches && Math.abs(index - this.index) > 1) return;
        const template = slide.querySelector('template[data-evp-deferred-image]');
        if (template) template.replaceWith(template.content.cloneNode(true));
      });
    }

    centerActiveDot() {
      const rail = this.dotRail;
      const dot = this.dots[this.index];
      if (!this.mobile.matches || !rail?.clientWidth || !dot) return;
      const maxScroll = rail.scrollWidth - rail.clientWidth;
      if (maxScroll <= 0) return;
      const railRect = rail.getBoundingClientRect();
      const dotRect = dot.getBoundingClientRect();
      const offset = dotRect.left + dotRect.width / 2 - railRect.left - rail.clientLeft - rail.clientWidth / 2;
      const target = rail.scrollLeft + offset;
      const rtl = getComputedStyle(rail).direction === 'rtl';
      const next = rtl ? Math.max(-maxScroll, Math.min(0, target)) : Math.max(0, Math.min(maxScroll, target));
      // Move only the dot strip; scrollIntoView would also move the product/page.
      if (Math.abs(next - rail.scrollLeft) > 1) rail.scrollLeft = next;
    }

    updateGallery() {
      if (!this.track.clientWidth) return;
      this.index = Math.round(Math.abs(this.track.scrollLeft) / this.track.clientWidth);
      this.hydrateGallery();
      this.dots.forEach((dot, index) => {
        if (index === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      this.centerActiveDot();
    }
  }
  customElements.define('edvi-product-detail', EdviProductDetail);

  class EdviProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.controller || this.dataset.loaded === 'true') return;
      this.controller = new AbortController();
      this.observer = new IntersectionObserver(entries => {
        if (!entries[0].isIntersecting) return;
        this.observer.disconnect();
        this.load();
      }, { rootMargin: '400px' });
      this.observer.observe(this);
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }
    async load() {
      try {
        const url = new URL(this.dataset.url, location.origin);
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) return;
        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = documentFragment.querySelector('edvi-product-recommendations');
        if (content?.querySelector('.evp-related__card')) this.innerHTML = content.innerHTML;
        else if (!this.querySelector('.evp-related__card')) this.hidden = true;
        this.dataset.loaded = 'true';
      } catch (error) {
        if (error.name !== 'AbortError') this.dataset.loadError = 'true';
      }
    }
  }
  customElements.define('edvi-product-recommendations', EdviProductRecommendations);
})();
